/*
 * @providesModule DynamicCheckbox
 */

import React, {useState} from 'react';
import {Text, TouchableOpacity, View, ViewStyle, TextStyle, StyleProp} from 'react-native';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';

export interface DynamicCheckboxProps {
  /** The text to display next to the checkbox */
  text: string;
  /** Initial checked state */
  initialChecked?: boolean;
  /** Callback when checkbox state changes */
  onToggle?: (isChecked: boolean) => void;
  /** Custom text style */
  textStyle?: StyleProp<TextStyle>;
  /** Custom container style */
  containerStyle?: StyleProp<ViewStyle>;
  /** Custom checkbox style */
  checkboxStyle?: StyleProp<ViewStyle>;
  /** Accessibility label for the checkbox */
  accessibilityLabel?: string;
  /** Test ID for testing */
  testID?: string;
  /** Custom checkbox color when checked */
  checkedColor?: string;
  /** Custom checkbox border color */
  borderColor?: string;
  /** Show checkbox on left or right side */
  checkboxPosition?: 'left' | 'right';
  /** Whether the checkbox is disabled */
  disabled?: boolean;
}

export const DynamicCheckbox: React.FC<DynamicCheckboxProps> = ({
  text,
  // needs to be checked whether consumed from BE or not
  initialChecked = true,
  onToggle,
  textStyle,
  containerStyle,
  checkboxStyle,
  accessibilityLabel,
  testID,
  checkedColor = 'rgb(74, 144, 226)',
  borderColor,
  checkboxPosition = 'left',
  disabled = false,
}) => {
  const [isChecked, setIsChecked] = useState(initialChecked);

  const toggleChecked = () => {
    if (disabled) return;

    const newState = !isChecked;
    setIsChecked(newState);
    onToggle?.(newState);
  };

  const styles = createStyles(isChecked, checkedColor, borderColor);

  const renderCheckbox = () => (
    <TouchableOpacity
      style={[styles.checkboxContainer, checkboxStyle]}
      onPress={toggleChecked}
      accessibilityRole={'button'}
      accessible={true}
      accessibilityLabel={accessibilityLabel || `checkbox ${isChecked ? 'checked' : 'unchecked'}`}
      testID={testID}
      disabled={disabled}
      hitSlop={{top: 10, left: 10, bottom: 10, right: 10}}
    >
      <View style={[styles.checkboxWrapper, disabled && styles.checkboxDisabled]}>
        {isChecked && (
          <SkillzImage
            source={'ic_checkmark_green_skz.png'}
            color={checkedColor}
            resizeMode={'contain'}
            style={styles.checkboxIcon}
          />
        )}
      </View>
    </TouchableOpacity>
  );

  const renderText = () => (
    <TouchableOpacity onPress={toggleChecked} activeOpacity={1} disabled={disabled}>
      <Text
        allowFontScaling={false}
        style={[styles.text, textStyle, disabled && styles.textDisabled]}
      >
        {text}
      </Text>
    </TouchableOpacity>
  );

  const content =
    checkboxPosition === 'left' ? (
      <>
        {renderCheckbox()}
        {renderText()}
      </>
    ) : (
      <>
        {renderText()}
        {renderCheckbox()}
      </>
    );

  return (
    <View
      style={[
        styles.container,
        containerStyle,
        checkboxPosition === 'right' && styles.containerReverse,
      ]}
    >
      {content}
    </View>
  );
};

const createStyles = (isChecked: boolean, checkedColor: string, borderColor?: string) => {
  const {textColorPrimary, playButtonGradient, textColorTertiary} =
    SkillzStyle.getOverriddenFallbackTheme();

  return {
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 16,
    },
    containerReverse: {
      flexDirection: 'row-reverse',
    },
    checkboxContainer: {
      marginRight: 12,
      marginTop: 2,
    },
    checkboxWrapper: {
      height: 24,
      width: 24,
      borderRadius: 4,
      backgroundColor: isChecked ? playButtonGradient[1] : 'white',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    checkboxDisabled: {
      opacity: 0.5,
      backgroundColor: textColorTertiary?.color,
    },
    checkboxIcon: {
      height: 14,
      width: 14,
      alignSelf: 'center',
      backgroundColor: 'transarent',
    },
    text: {
      fontSize: 12,
      fontFamily: 'Open Sans',
      fontWeight: '400',
      lineHeight: 22,
      fontStyle: 'normal',
      color: textColorPrimary?.color || '#000',
      //   flex: 1,
      width: '90%',
    },
    textDisabled: {
      opacity: 0.5,
      color: textColorTertiary?.color,
    },
  };
};

export default DynamicCheckbox;
